import type { Block, DesignBlock, MatchBlock, QuizBlock } from '@/types/blocks'
import type { ContentLang } from '../editorSlice'
import { sortByOrder } from './orderUtils'
import { splitFillBlank } from '../components/blocks/previews/fillBlankSplit'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import { hasEmptyDesignText, hasEmptyTableCell } from './designTextIssues'
import { LANG_CODE, overflowRows } from './publishOverflowIssues'

export { overflowSlot } from './publishOverflowIssues'

/** Экран урока, который не даст опубликовать курс. Номера — с единицы. */
export interface BlockIssue {
  lesson: number
  /** `null` — проблема у урока целиком (в нём нет блоков). */
  screen: number | null
  problem: string
  /** «RU»/«EN» — чего не хватает на этом языке; «—» — от языка не зависит. */
  lang: string
  /** Куда вести по «Перейти». */
  lessonId: number
  blockId: number | null
  /** Проблема, которую валидатор api не проверит (переполнение меряет только
   *  браузер): «Обновить» обязано гаснуть по ней само. */
  clientOnly?: true
  /** Рекомендация (жёлтое переполнение): публикации не мешает, `clientOnly` у такой нет. */
  warning?: true
}

const NO_LANG = '—'

/** Пустая строка, `null` и одни пробелы — «нет текста». */
const blank = (v: string | null | undefined): boolean => !v?.trim()

/** Колонка = язык: `*En` — английский слот, без суффикса — русский. */
const quizBody = (block: QuizBlock, lang: ContentLang) =>
  lang === 'en' ? block.bodyEn : block.body

/** Сколько вариантов нужно квизу (решение заказчика): несколько ответов — 3, остальным — 2. */
const minOptions = (block: QuizBlock): number => (block.multiAnswer ? 3 : 2)

/**
 * Проблемы, которые от языка не зависят: число вариантов и
 * выбранный правильный ответ. Варианты и отметка «верный» читаются из слота
 * ОРИГИНАЛА (`source`): у английского курса он в `bodyEn`.
 */
const neutralProblems = (block: Block, source: ContentLang): string[] => {
  const m = messagesNow(messages)
  // Составной без единого элемента — ученик увидел бы пустой экран.
  if (block.type === 'design') return (block.elements ?? []).length === 0 ? [m.emptyDesign] : []
  if (block.type === 'match') return block.pairs.length < 2 ? [m.tooFewPairs] : []
  if (block.type !== 'quiz') return []
  if (block.quizType === 'yes_no') return block.correctAnswer === null ? [m.noCorrectAnswer] : []
  const answers = quizBody(block, source)?.answers ?? []
  const min = minOptions(block)
  const correct = answers.filter((a) => a.isCorrect).length
  // «Несколько ответов» с одним правильным — по сути квиз «Один ответ»
  // (решение заказчика): правильных нужно минимум два.
  const correctProblem = block.multiAnswer
    ? correct < 2
      ? [m.tooFewCorrect]
      : []
    : correct === 0
      ? [m.noCorrectAnswer]
      : []
  return [...(answers.length < min ? [m.tooFewAnswers(min)] : []), ...correctProblem]
}

const quizLangProblems = (block: QuizBlock, lang: ContentLang): string[] => {
  const m = messagesNow(messages)
  const body = quizBody(block, lang)
  // У «Заполни пропуск» вопрос — это утверждение, и проверяется в нём ПРОПУСК
  // (`___`): без него ученику нечего заполнять, пустое утверждение — тоже без пропуска.
  const questionProblem =
    block.quizType === 'fill_blank'
      ? splitFillBlank(body?.question ?? '').hasGap
        ? []
        : [m.noGap]
      : blank(body?.question)
        ? [m.noQuestion]
        : []
  return [
    ...questionProblem,
    ...((body?.answers ?? []).some((a) => blank(a.text)) ? [m.emptyAnswers] : []),
  ]
}

const matchLangProblems = (block: MatchBlock, en: boolean): string[] =>
  block.pairs.some((p) =>
    en ? blank(p.leftTextEn) || blank(p.rightTextEn) : blank(p.leftText) || blank(p.rightText),
  )
    ? [messagesNow(messages).emptyPairs]
    : []

// Список саб-блоков у составного ОБЩИЙ на оба языка (контракт): элемент
// «Картинка» показывается в каждом языке, поэтому файл нужен на каждом.
const designLangProblems = (block: DesignBlock, en: boolean): string[] => {
  const m = messagesNow(messages)
  const elements = block.elements ?? []
  return [
    ...(elements.some((el) => el.type === 'image' && blank(en ? el.imageUrlEn : el.imageUrl))
      ? [m.noDesignImage]
      : []),
    ...(elements.some((el) => hasEmptyDesignText(el, en)) ? [m.emptyDesignText] : []),
    ...(elements.some((el) => hasEmptyTableCell(el, en)) ? [m.incompleteTable] : []),
  ]
}

/** Что не заполнено на ЭТОМ языке: вопрос, тексты вариантов и пар, картинки. */
const langProblems = (block: Block, lang: ContentLang): string[] => {
  const m = messagesNow(messages)
  const en = lang === 'en'
  switch (block.type) {
    case 'quiz':
      return quizLangProblems(block, lang)
    case 'match':
      return matchLangProblems(block, en)
    case 'info':
      return blank(en ? block.imageEn : block.image) ? [m.noDesignImage] : []
    // Ролик — на каждом языке, как картинка: у EN свой слот `videoIdEn`.
    case 'video':
      return blank(en ? block.videoIdEn : block.videoId) ? [m.noVideo] : []
    case 'design':
      return designLangProblems(block, en)
    default:
      return []
  }
}

/**
 * Экраны урока, которые не дадут опубликовать курс. `langs` — языки, на которых
 * курс обязан быть заполнен (оригинал первым): вопрос, варианты ответа и
 * картинки проверяются на КАЖДОМ из них. Номера позиционные — как на миниатюре.
 * `overflow` — слоты (`overflowSlot`) составных блоков, которые по замеру не
 * помещаются на экран (`over`) или близки к границе (`warn`); меряет зонд урока,
 * здесь только строка списка.
 */
export const lessonBlockIssues = (
  lessonId: number,
  lessonRank: number,
  blocks: Block[],
  langs: ContentLang[],
  overflow: ReadonlyMap<string, 'warn' | 'over'> = new Map(),
): BlockIssue[] =>
  // Пустой урок ловит `lessonFieldIssues` (по счётчику блоков) — здесь не дублируем.
  sortByOrder(blocks).flatMap((block, i) => {
    const at = { lesson: lessonRank + 1, screen: i + 1, lessonId, blockId: block.id }
    const source = langs[0] ?? 'ru'
    return [
      ...neutralProblems(block, source).map((problem) => ({ ...at, problem, lang: NO_LANG })),
      ...langs.flatMap((lang) => [
        ...langProblems(block, lang).map((problem) => ({ ...at, problem, lang: LANG_CODE[lang] })),
        ...(block.type === 'design' ? overflowRows(at, overflow, block.id, lang) : []),
      ]),
    ]
  })
